import { RefObject, useCallback, useEffect, useRef } from 'react';

import type { Chart } from 'highcharts';
import type HighchartsReact from 'highcharts-react-official';

import {
  getWheelGesture,
  isZoomedIn,
  panRange,
  zoomRange,
  type ZoomRange,
} from './chartZoom.functions.ts';

/**
 * Wheel zoom and pan on the chart's x-axis, never zooming out past the original range.
 * The component sets the original range through `applyBaseRange` (resets any zoom) and
 * `followBaseRange` (live charts: slides the range unless the user has zoomed in).
 */
export const useChartZoom = (highChartsRef: RefObject<HighchartsReact.RefObject | null>) => {
  const baseRangeRef = useRef<ZoomRange | null>(null);
  const isZoomedRef = useRef(false);

  const applyBaseRange = useCallback(
    ({ min, max }: { min: number | undefined; max: number | undefined }) => {
      const axis = highChartsRef.current?.chart?.xAxis[0];
      if (!axis) return;
      unlockYAxes(highChartsRef.current?.chart);
      axis.setExtremes(min, max, true, true);
      const extremes = axis.getExtremes();
      baseRangeRef.current =
        extremes.min !== undefined && extremes.max !== undefined
          ? { min: extremes.min, max: extremes.max }
          : null;
      isZoomedRef.current = false;
    },
    [highChartsRef]
  );

  const followBaseRange = useCallback(
    ({ min, max }: ZoomRange) => {
      if (!isZoomedRef.current) {
        applyBaseRange({ min, max });
        return;
      }
      baseRangeRef.current = { min, max };
      // Keep the range, but draw the points added since the last redraw.
      highChartsRef.current?.chart?.redraw();
    },
    [applyBaseRange, highChartsRef]
  );

  // Runs after every render and attaches once per chart container, so a chart that is created
  // after the first render (or re-created) still gets the listener.
  const attachedRef = useRef<{ container: HTMLElement; detach: () => void } | null>(null);

  useEffect(() => {
    const chart = highChartsRef.current?.chart;
    const container = chart?.container;
    if (!chart || !container || attachedRef.current?.container === container) return;
    attachedRef.current?.detach();

    const onWheel = (event: WheelEvent) => {
      const base = baseRangeRef.current;
      const axis = chart.xAxis[0];
      const gesture = getWheelGesture(event);
      if (!base || !axis || !gesture) return;

      event.preventDefault();

      const { min, max } = axis.getExtremes();
      if (min === undefined || max === undefined) return;
      const range = { min, max };

      const next =
        gesture.type === 'zoom'
          ? zoomRange({
              base,
              range,
              factor: gesture.factor,
              anchorRatio: Math.min(
                Math.max(
                  (event.clientX - container.getBoundingClientRect().left - chart.plotLeft) /
                    chart.plotWidth,
                  0
                ),
                1
              ),
            })
          : panRange({ base, range, delta: (gesture.deltaPx / chart.plotWidth) * (max - min) });

      if (next.min === min && next.max === max) return;

      // Freeze the y-axes at their full-range scale while zoomed; release them at 100%.
      const isZoomedNext = isZoomedIn({ base, range: next });
      if (isZoomedNext && !isZoomedRef.current) lockYAxes(chart);
      if (!isZoomedNext) unlockYAxes(chart);
      isZoomedRef.current = isZoomedNext;

      axis.setExtremes(next.min, next.max, true, false);
    };

    // Not passive: preventDefault stops the page scrolling and the browser zooming on pinch.
    container.addEventListener('wheel', onWheel, { passive: false });
    attachedRef.current = {
      container,
      detach: () => container.removeEventListener('wheel', onWheel),
    };
  });

  useEffect(
    () => () => {
      attachedRef.current?.detach();
      attachedRef.current = null;
    },
    []
  );

  return { applyBaseRange, followBaseRange };
};

const lockYAxes = (chart: Chart) =>
  chart.yAxis.forEach(axis => {
    const { min, max } = axis.getExtremes();
    axis.setExtremes(min, max, false, false);
  });

const unlockYAxes = (chart: Chart | undefined) =>
  chart?.yAxis.forEach(axis => axis.setExtremes(undefined, undefined, false, false));
